import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconArrowUp } from '@calendly/ui/components/glyphs/IconArrowUp';
import { IconPlayerStopFilled } from '@calendly/ui/components/glyphs/IconPlayerStopFilled';
import { IconButton } from '@calendly/ui/components/icon-button';
import classNames from 'classnames';
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { IconEditWrite } from '../assets';
import { ChatSuggestions } from '../chatSuggestions';
import { CHAT_SUGGESTIONS } from '../constants';

// Configuration constants
const LINE_HEIGHT = 24; // Height of a single line in pixels
const MAX_LINES = 7; // Maximum number of lines before scrolling
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES; // Calculate max height dynamically

const backgroundWrapperCss = "b1uc8lua";
const newChatButtonContainerCss = "neq0lam";
const iconContainerCss = "i1izk930";
const growableSearchContainerCss = "g9gg2ob";
const growableSearchContainerActiveCss = "gkd0xyd";
const embeddedGrowableSearchContainerCss = "e1inv68l";
const embeddedGrowableSearchContainerActiveCss = "ezk6qmb";
const chatSuggestionsContainerCss = "c1v2ilmg";
const searchWrapperCss = "s1ns6cjl";
const embeddedSearchWrapperCss = "e1ibz1ar";
const whiteFadeCss = "w98gj7e";
const textareaWrapperCss = "thg8n7p";
const growableTextareaCss = "g27neye";
const bottomRowCss = "bnpdadq";
const suggestionsFadeInCss = "s1kg7hnp";
const suggestionsVisibleCss = "s7xw6mv";
export const GrowableSearchSection = forwardRef(({
  searchQuery,
  onSearchChange,
  onSearch,
  location,
  showSuggestions = false,
  onSuggestionClick = () => {},
  responseBeingGenerated = false,
  onCancelGeneration = () => {},
  includeWhiteFade = true,
  analyticsHandlers = {},
  onNewChatPressed,
  embedded = false
}, ref) => {
  const textareaRef = useRef(null);
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false);
  const [isAnimatingSuggestions, setIsAnimatingSuggestions] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Expose focus function to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }), []);
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Handle fade-in animation when response generation stops
  useEffect(() => {
    if (showSuggestions && !responseBeingGenerated && !shouldShowSuggestions) {
      setIsAnimatingSuggestions(true);
      setShouldShowSuggestions(true);
    } else if (!showSuggestions || responseBeingGenerated) {
      setShouldShowSuggestions(false);
      setIsAnimatingSuggestions(false);
    }
  }, [showSuggestions, responseBeingGenerated, shouldShowSuggestions]);
  const resetTextareaSize = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = `${LINE_HEIGHT}px`;
      textarea.style.overflowY = 'hidden';
    }
  };
  const handleSearch = e => {
    e.stopPropagation(); // Prevent event bubbling to container
    if (responseBeingGenerated) {
      onCancelGeneration();
    } else {
      onSearch(searchQuery);
      resetTextareaSize();
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default newline behavior
      if (responseBeingGenerated || !searchQuery || searchQuery.trim() === '') return;
      handleSearch(e);
    }
    // If it's Shift+Enter, let the default behavior happen (new line)
  };
  const handleInputChange = e => {
    const textarea = e.target;
    const value = e.target.value;

    // Reset height to minHeight first
    textarea.style.height = `${LINE_HEIGHT}px`;

    // Calculate content height without padding
    const computedStyle = window.getComputedStyle(textarea);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom;
    if (contentHeight > LINE_HEIGHT) {
      if (contentHeight > MAX_HEIGHT) {
        textarea.style.height = `${MAX_HEIGHT}px`;
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${contentHeight + paddingTop + paddingBottom}px`;
        textarea.style.overflowY = 'hidden';
      }
    } else {
      textarea.style.overflowY = 'hidden';
    }
    onSearchChange(value);
  };
  const handleContainerClick = () => {
    // Focus the textarea when the container is clicked
    if (textareaRef.current) {
      textareaRef.current.focus();

      // Track textarea click
      if (analyticsHandlers.trackTextboxClick) {
        analyticsHandlers.trackTextboxClick(location);
      }
    }
  };
  const handleFocus = () => {
    setIsActive(true);
  };
  const handleBlur = () => {
    setIsActive(false);
  };
  const isSearchDisabled = !searchQuery || searchQuery.trim() === '';
  return <div className={embedded ? embeddedSearchWrapperCss : searchWrapperCss}>
      {includeWhiteFade && <div aria-hidden className={whiteFadeCss} data-testid="white-fade" />}
      {shouldShowSuggestions && CHAT_SUGGESTIONS.length > 0 && <div className={chatSuggestionsContainerCss}>
          <div className={isAnimatingSuggestions ? suggestionsFadeInCss : suggestionsVisibleCss}>
            <ChatSuggestions chatSuggestions={CHAT_SUGGESTIONS} onSuggestionClick={onSuggestionClick} embedded={embedded} />
          </div>
        </div>}
      {onNewChatPressed && !embedded && <div className={newChatButtonContainerCss}>
        <Button decoration="secondary-outline" onClick={onNewChatPressed} size="small">
          <div className={iconContainerCss}>
            <IconEditWrite />
          </div>
          {I18n.t('publisher.agent_hub.new_chat')}
        </Button>
      </div>}
      <div className={backgroundWrapperCss}>
        <div className={classNames(embedded ? embeddedGrowableSearchContainerCss : growableSearchContainerCss, isActive && (embedded ? embeddedGrowableSearchContainerActiveCss : growableSearchContainerActiveCss))} data-testid="composer-surface" data-active={String(isActive)} onClick={handleContainerClick}>
          <div className={textareaWrapperCss}>
            <textarea className={growableTextareaCss} placeholder={I18n.t('publisher.agent_hub.composer.placeholder')} value={searchQuery} onChange={handleInputChange} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} ref={textareaRef} />
          </div>
          <div className={bottomRowCss}>
            <IconButton appearance="primary" size="medium" icon={responseBeingGenerated ? IconPlayerStopFilled : IconArrowUp} title={I18n.t(responseBeingGenerated ? 'publisher.agent_hub.composer.stop_generation' : 'publisher.action.send')} data-testid="composer-send" data-embedded={String(embedded)} onClick={handleSearch} disabled={isSearchDisabled && !responseBeingGenerated} />
          </div>
        </div>
      </div>
    </div>;
});

require("./GrowableSearchSection.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./GrowableSearchSection.js");